import * as React from 'react';
import { Component, Fragment } from 'react';
import { observer } from 'mobx-react';
import { inject } from '../utils';
import { withTheme } from 'styled-components';

import { ConditionalDisclaimers } from '../models/configuration/disclaimers/ConditionalDisclaimers';
import { DisclaimerDisplayType } from '../models/configuration/disclaimers/DisclaimerDisplayType';

import Tooltip from './Tooltip';
import Superscript from './Superscript';

import {
    DataStore,
    DisclaimerStore,
    EntityStore,
    SuperscriptScope,
} from '../stores';
import { AnalyticsContext } from '../models/analyticsContext';
import { Theme } from '../models/Theme';

export interface InlineDisclaimersProps {
    theme?: Theme;
    dataStore?: DataStore;
    entityStore?: EntityStore;
    conditionalDisclaimers: ConditionalDisclaimers;
    superscriptScope: SuperscriptScope;
    superscriptContext?: string;
    accessibilityContext?: string;
    analyticsContext?: AnalyticsContext;
    forceSuperScripts?: boolean;
    metricContext?: string;
}

@inject('dataStore', 'entityStore')
@observer
class InlineDisclaimers extends Component<InlineDisclaimersProps> {
    loadDisclaimers = (
        callback: (
            disclaimerStore: DisclaimerStore,
            entity: any,
            conditionalDisclaimers: ConditionalDisclaimers
        ) => any,
        defaultValue?: any
    ) => {
        const {
            conditionalDisclaimers,
            dataStore,
            entityStore,
            forceSuperScripts,
        } = this.props;

        const entity = entityStore?.value;
        if (conditionalDisclaimers && conditionalDisclaimers.length > 0) {
            let updatedConditionalDisclaimers = conditionalDisclaimers;
            if (forceSuperScripts) {
                updatedConditionalDisclaimers = conditionalDisclaimers.map(
                    disclaimer => {
                        return {
                            ...disclaimer,
                            display: DisclaimerDisplayType.superscript,
                        };
                    }
                );
            }
            const disclaimerStore = (dataStore as DataStore).disclaimerStore;
            if (disclaimerStore && callback) {
                return (
                    callback(
                        disclaimerStore,
                        entity,
                        updatedConditionalDisclaimers
                    ) || defaultValue
                );
            }
        }
        return defaultValue;
    };
    loadTooltipDisclaimers(content: any) {
        const { accessibilityContext, analyticsContext } = this.props;
        return this.loadDisclaimers(
            (disclaimerStore, entity, conditionalDisclaimers) => {
                const tooltipDisclaimers = disclaimerStore.getTooltipDisclaimers(
                    entity,
                    conditionalDisclaimers
                );
                if (tooltipDisclaimers && tooltipDisclaimers.length > 0) {
                    const tooltipContent = [];
                    const types = new Set();
                    for (const meta of tooltipDisclaimers) {
                        tooltipContent.push(meta.text);
                        types.add(meta.type);
                    }
                    return (
                        <Fragment>
                            {content}
                            <Tooltip
                                content={tooltipContent}
                                accessibilityContext={accessibilityContext}
                                analyticsContext={{
                                    tooltipCode: types,
                                    source: 'InlineDisclaimers',
                                    ...analyticsContext,
                                }}
                            />
                        </Fragment>
                    );
                }
            },
            content
        );
    }
    loadDisclaimerSuperscripts(content: any) {
        const { theme, superscriptScope, analyticsContext } = this.props;
        return this.loadDisclaimers(
            (disclaimerStore, entity, conditionalDisclaimers) => {
                const superscripts = disclaimerStore.getSuperscripts(
                    entity,
                    conditionalDisclaimers,
                    superscriptScope
                );
                if (superscripts && superscripts.length > 0) {
                    return (
                        <Fragment>
                            {content}
                            <Superscript
                                superscripts={superscripts}
                                superscriptContext={
                                    this.props.superscriptContext
                                }
                                {...theme.Superscript}
                                analyticsContext={analyticsContext}
                            />
                        </Fragment>
                    );
                }
            },
            content || ''
        );
    }
    render() {
        const { children } = this.props;
        return this.loadTooltipDisclaimers(
            this.loadDisclaimerSuperscripts(children)
        );
    }
}

export default withTheme(InlineDisclaimers);



// WEBPACK FOOTER //
// ./src/Components/InlineDisclaimers.tsx